import type { AssignableRole, Invitation, Role } from "@unbox/api";
import { Edit01, Trash01, Users01 } from "@untitledui/icons";
import { useEffect, useMemo, useState } from "react";
import { Heading } from "react-aria-components";
import { useListData } from "react-stately";
import { useAdminInvites } from "@/api/admin/invites";
import { useAdminUsers } from "@/api/admin/users";
import { useAddUsersToWorkspace, useRemoveUserFromWorkspace } from "@/api/admin/workspace-mutations";
import { LogoStack } from "@/components/admin/logo-stack";
import { EffectiveRoleLabel, InviteRoleLabel, type ScopedRoleValue } from "@/components/admin/role-label";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { PaginationPageMinimalCenter } from "@/components/application/pagination/pagination";
import { Table } from "@/components/application/table/table";
import { Avatar } from "@/components/base/avatar/avatar";
import { Button } from "@/components/base/buttons/button";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { CloseButton } from "@/components/base/buttons/close-button";
import { MultiSelect } from "@/components/base/select/multi-select";
import { FormItem } from "@/components/ui/form";
import { useDebounce } from "@/hooks/use-debounce";
import { usePermissions } from "@/hooks/use-permissions";
import { WorkspaceRoleTree, type WorkspaceRoleTreeInfo } from "@/pages/admin/components/workspace-role-tree";
import { useWorkspaceRoleTree } from "@/pages/admin/hooks/use-workspace-role-tree";
import { TabHeaderReadOnly } from "@/pages/admin/merchant-detail/tabs/tab-header";
import { EditUserModal } from "@/pages/admin/modals/edit-user";
import { ResendInviteModal } from "@/pages/admin/modals/resend-invite";
import { RevokeInviteModal } from "@/pages/admin/modals/revoke-invite";
import { error, success } from "@/providers/ToastProvider";
import { formatDate, toDateOrNull } from "@/utils/date-conversion";
import { getUserInitials } from "@/utils/user-initials";

const PAGE_SIZE = 20;

type WorkspaceUser = {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: Role;
  effectiveRole: ScopedRoleValue;
  // Role scoped to this workspace, incl. the derived "multiple" (UNB-2001).
  workspaceRole: ScopedRoleValue;
  createdAt: string;
  tosDate?: string | null;
  lastActivity?: string | null;
  merchants: { id: string; name: string }[];
};

type WorkspaceMerchant = { id: string; name: string; logoUrl?: string | null; region?: string | null };

type AllUsersTabProps = {
  workspace: {
    id: string;
    name: string;
    users: WorkspaceUser[];
    merchants: WorkspaceMerchant[];
  };
};

type UserRow = { type: "user" } & WorkspaceUser;

type InviteRow = {
  type: "invite";
  id: string;
  email: string;
  role: Role;
  assignableRole: AssignableRole;
  createdAt: string;
  expiresAt: string;
  status: "pending" | "accepted" | "revoked";
  expired: boolean;
  invite: Pick<Invitation, "id" | "status">;
};

type TableRow = UserRow | InviteRow;

function formatRelativeTime(date: Date | null): string {
  if (!date) return "—";
  const diff = Date.now() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  return `${days} days ago`;
}

function getEmailInitials(email: string) {
  const name = email.split("@")[0];
  const parts = name.split(/[._-]/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

const AddUsersModal = ({ workspace }: { workspace: WorkspaceRoleTreeInfo }) => {
  const [open, setOpen] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const debouncedUserSearch = useDebounce(userSearch, 300);
  const { data: usersData } = useAdminUsers(
    { offset: 0, limit: 50 },
    { column: "firstName", direction: "asc" },
    { text: debouncedUserSearch },
  );
  const addUsers = useAddUsersToWorkspace();
  const { invitableRoles } = usePermissions();
  const selectedUsers = useListData<{ id: string; label: string; supportingText?: string }>({ initialItems: [] });

  // UNB-2001 — single, fixed workspace with its merchants underneath. The tree
  // holds one entry keyed by this workspace; every merchant defaults to "user".
  const roleTree = useWorkspaceRoleTree();
  const workspacesById = useMemo(() => new Map([[workspace.id, workspace]]), [workspace]);

  useEffect(() => {
    selectedUsers.removeSelectedItems();
    for (const key of [...selectedUsers.selectedKeys]) {
      selectedUsers.remove(key as string);
    }
    setUserSearch("");
    roleTree.setGrants({
      [workspace.id]: {
        workspaceAdmin: false,
        merchants: Object.fromEntries(workspace.merchants.map((m) => [m.id, "user" as const])),
      },
    });
  }, [open]);

  const handleAdd = async () => {
    if (selectedUsers.items.length === 0) {
      error("Select at least one user");
      return;
    }
    const grant = roleTree.grants[workspace.id];
    // Workspace admins need no brands (UNB-2001); everyone else needs ≥1.
    if (!grant || (!grant.workspaceAdmin && Object.keys(grant.merchants).length === 0)) {
      error("Select at least one merchant for these users");
      return;
    }
    try {
      await addUsers.mutateAsync({
        workspaceId: workspace.id,
        userIds: selectedUsers.items.map((u) => u.id),
        merchants: grant.workspaceAdmin ? {} : grant.merchants,
        workspaceAdmin: grant.workspaceAdmin,
      });
      success("Users added to workspace");
      setOpen(false);
    } catch {
      error("Failed to add users");
    }
  };

  return (
    <>
      <Button color="primary" onClick={() => setOpen(true)}>
        Add new User
      </Button>
      {/* Not dismissable: an outside click would discard the whole form (UNB-2129). */}
      <ModalOverlay isOpen={open} onOpenChange={setOpen} isDismissable={false}>
        <Modal className="dark-mode w-[640px] max-w-full">
          <Dialog>
            <div className="relative w-full overflow-hidden rounded-2xl bg-primary shadow-none">
              <CloseButton onClick={() => setOpen(false)} theme="light" size="lg" className="absolute top-3 right-3" />
              <div className="flex flex-col gap-4 px-4 pt-5 mr-4 sm:px-6 sm:pt-6">
                <Heading slot="title" className="text-md font-semibold text-primary">
                  Add Users to Workspace
                </Heading>
              </div>
              <div className="h-5 w-full" />
              <div className="relative flex flex-col gap-4 px-4 sm:gap-5 sm:px-6">
                <FormItem className="flex flex-col">
                  <MultiSelect
                    label="Users"
                    placeholder="Search users"
                    size="md"
                    items={
                      usersData?.users.map((u) => ({
                        id: u.id,
                        label: `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.email,
                        supportingText: u.email,
                      })) ?? []
                    }
                    selectedItems={selectedUsers}
                    popoverClassName="dark-mode"
                    onInputChange={setUserSearch}
                  >
                    {(item) => (
                      <MultiSelect.Item
                        key={item.id}
                        id={item.id}
                        label={item.label}
                        supportingText={item.supportingText}
                        className="dark-mode"
                      />
                    )}
                  </MultiSelect>
                </FormItem>
                <FormItem className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-primary">Role</span>
                  <WorkspaceRoleTree
                    grants={roleTree.grants}
                    workspacesById={workspacesById}
                    invitableRoles={invitableRoles}
                    onToggleBrand={roleTree.toggleBrand}
                    onSetWorkspaceRole={roleTree.setWorkspaceRole}
                    onSetMerchantRole={roleTree.setMerchantRole}
                  />
                </FormItem>
              </div>
              <div className="flex w-full items-center justify-end gap-3 px-4 py-4 sm:px-6 sm:py-4">
                <Button size="md" color="secondary" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button size="md" color="primary" onClick={handleAdd} isDisabled={addUsers.isPending}>
                  {addUsers.isPending ? "Adding..." : "Add Users"}
                </Button>
              </div>
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </>
  );
};

export const AllUsersTab = ({ workspace }: AllUsersTabProps) => {
  const removeUser = useRemoveUserFromWorkspace();
  const [page, setPage] = useState(1);
  const [editUser, setEditUser] = useState<UserRow | undefined>(undefined);

  const { data: invitesData } = useAdminInvites(
    { limit: 100, offset: 0 },
    { column: "createdAt", direction: "desc" },
    { workspaceId: workspace.id, status: "pending" },
  );

  const pendingInvites = useMemo(() => {
    if (!invitesData?.invitations) return [];
    return invitesData.invitations.filter((inv) => inv.status === "pending");
  }, [invitesData?.invitations]);

  const tableRows: TableRow[] = useMemo(() => {
    const userRows: UserRow[] = workspace.users.map((u) => ({ type: "user" as const, ...u }));
    const inviteRows: InviteRow[] = pendingInvites.map((inv) => ({
      type: "invite" as const,
      id: `invite-${inv.id}`,
      email: inv.email,
      role: inv.role,
      assignableRole: inv.assignableRole,
      createdAt: inv.createdAt,
      expiresAt: inv.expiresAt,
      status: inv.status,
      expired: new Date(inv.expiresAt) <= new Date(),
      invite: { id: inv.id, status: inv.status },
    }));
    return [...inviteRows, ...userRows];
  }, [workspace.users, pendingInvites]);

  const totalPages = Math.ceil(tableRows.length / PAGE_SIZE);
  const paginatedRows = tableRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleRemove = async (userId: string) => {
    try {
      await removeUser.mutateAsync({ workspaceId: workspace.id, userId });
      success("User removed from workspace");
    } catch {
      error("Failed to remove user");
    }
  };

  const logoMap = new Map(workspace.merchants.map((m) => [m.id, m.logoUrl ?? null]));
  const totalBrands = workspace.merchants.length;

  return (
    <div className="flex flex-col gap-6 h-full">
      <TabHeaderReadOnly
        title={`${tableRows.length} Users`}
        subtitle="Users granted access to this workspace and every merchant within it."
        rightContent={<AddUsersModal workspace={workspace} />}
      />

      <div className="border-t border-primary" />

      {tableRows.length > 0 ? (
        <>
          <Table aria-label="Workspace users">
            <Table.Header className="bg-transparent">
              <Table.Head id="name" label="Name" isRowHeader className="w-[28%]" />
              <Table.Head id="role" label="Role" className="w-[15%]" />
              <Table.Head id="canView" label="Can view" className="w-[25%]" />
              <Table.Head id="status" label="Status" className="w-[20%]" />
              <Table.Head id="actions" label="" className="w-[12%]" />
            </Table.Header>

            <Table.Body items={paginatedRows}>
              {(item) => {
                if (item.type === "invite") {
                  return (
                    <Table.Row id={item.id} className="even:bg-secondary">
                      <Table.Cell className="whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <Avatar size="sm" initials={getEmailInitials(item.email)} alt={item.email} />
                          <div className="flex flex-col">
                            <p className="text-sm font-medium text-primary">{item.email}</p>
                          </div>
                        </div>
                      </Table.Cell>
                      <Table.Cell className="whitespace-nowrap">
                        <InviteRoleLabel assignableRole={item.assignableRole} />
                      </Table.Cell>
                      <Table.Cell>
                        <span className="text-sm text-tertiary">—</span>
                      </Table.Cell>
                      <Table.Cell className="whitespace-nowrap">
                        <div className="flex flex-col gap-0.5">
                          {item.expired ? (
                            <>
                              <span className="text-sm font-medium text-warning-primary">Invite expired</span>
                              <span className="text-xs text-tertiary">{formatDate(item.createdAt)}</span>
                            </>
                          ) : (
                            <>
                              <span className="text-sm font-medium text-utility-blue-light-500">Invite sent</span>
                              <span className="text-xs text-tertiary">{formatDate(item.createdAt)}</span>
                            </>
                          )}
                        </div>
                      </Table.Cell>
                      <Table.Cell className="whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <ResendInviteModal invite={item.invite} />
                          <RevokeInviteModal invite={item.invite} />
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  );
                }

                const canViewItems = item.merchants.map((m) => ({
                  id: m.id,
                  name: m.name,
                  logoUrl: logoMap.get(m.id) ?? null,
                }));
                const canViewCount = canViewItems.length;
                const countLabel =
                  canViewCount === totalBrands ? `All ${totalBrands}` : `${canViewCount} of ${totalBrands}`;

                const lastActivity = toDateOrNull(item.lastActivity ?? null);
                const isActive = !!(item.tosDate || item.lastActivity);

                return (
                  <Table.Row id={item.id} className="even:bg-secondary">
                    <Table.Cell className="whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <Avatar
                          size="sm"
                          initials={getUserInitials(item.firstName, item.lastName)}
                          alt={`${item.firstName ?? ""} ${item.lastName ?? ""}`.trim() || item.email}
                        />
                        <div className="flex flex-col">
                          <p className="text-sm font-medium text-primary">
                            {item.firstName} {item.lastName}
                          </p>
                          <p className="text-sm text-tertiary">{item.email}</p>
                        </div>
                      </div>
                    </Table.Cell>
                    <Table.Cell className="whitespace-nowrap">
                      <EffectiveRoleLabel effectiveRole={item.effectiveRole} scopedRole={item.workspaceRole} />
                    </Table.Cell>
                    <Table.Cell>
                      <LogoStack items={canViewItems} countLabel={countLabel} />
                    </Table.Cell>
                    <Table.Cell className="whitespace-nowrap">
                      {isActive ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="text-sm font-medium text-brand-tertiary_alt">Active</span>
                          <span className="text-xs text-tertiary">
                            {lastActivity
                              ? `Last active ${formatRelativeTime(lastActivity)}`
                              : formatDate(item.createdAt)}
                          </span>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-0.5">
                          <span className="text-sm font-medium text-tertiary">Account pending</span>
                          <span className="text-xs text-tertiary">{formatDate(item.createdAt)}</span>
                        </div>
                      )}
                    </Table.Cell>
                    <Table.Cell className="whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <ButtonUtility
                          size="xs"
                          color="tertiary"
                          tooltip="Remove from workspace"
                          icon={Trash01}
                          onClick={() => handleRemove(item.id)}
                          isDisabled={removeUser.isPending}
                        />
                        <ButtonUtility
                          size="xs"
                          color="tertiary"
                          tooltip="Edit"
                          icon={Edit01}
                          onClick={() => setEditUser(item)}
                        />
                      </div>
                    </Table.Cell>
                  </Table.Row>
                );
              }}
            </Table.Body>
          </Table>

          {totalPages > 1 && (
            <PaginationPageMinimalCenter
              page={page}
              total={totalPages}
              onPageChange={setPage}
              className="px-4 py-3 md:px-6 md:pt-3 md:pb-4"
            />
          )}
        </>
      ) : (
        <div className="flex flex-col items-center justify-center gap-4 flex-1">
          <div className="flex items-center justify-center size-14 rounded-full bg-tertiary">
            <Users01 className="size-6 text-secondary" />
          </div>
          <div className="flex flex-col items-center gap-1 text-center">
            <p className="text-sm font-semibold text-primary">No users in this workspace</p>
            <p className="text-sm text-tertiary max-w-xs">
              Add users to this workspace to grant them access to the merchants within it.
            </p>
          </div>
        </div>
      )}
      {/* @ts-expect-error: workspace user is a subset of User; unused fields default safely */}
      <EditUserModal user={editUser} onClose={() => setEditUser(undefined)} />
    </div>
  );
};
